import { inject } from '@angular/core';
import {
	ActivatedRouteSnapshot,
	type CanDeactivateFn,
	NavigationCancel,
	NavigationEnd,
	NavigationError,
	NavigationSkipped,
	Router,
	RouterStateSnapshot
} from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs';
import { loadAppSuccess } from '@store/actions/app.actions';
import { ApplicationsService } from '@core/services/applications.service';

/**
 * Guard that clears the current app state when navigating between different app contexts.
 * Clears the state if the appId changes between routes.
 *
 * The state is cleared only once the navigation has actually finished: Angular
 * runs every `canDeactivate` guard of a navigation in parallel, so clearing it
 * right here would also wipe the app context of a navigation that another guard
 * goes on to cancel, leaving the user inside the app with no app menu.
 *
 * @param component - The component being deactivated
 * @param currentRoute - The current activated route snapshot
 * @param currentState - The current router state
 * @param nextState - The next router state (destination)
 * @returns true to allow navigation
 */
export const canDeactivateAppFn: CanDeactivateFn<any> = (
	_component,
	currentRoute: ActivatedRouteSnapshot,
	_currentState: RouterStateSnapshot,
	nextState?: RouterStateSnapshot
) => {
	const applicationsSvc = inject(ApplicationsService);
	const router = inject(Router);
	const store = inject(Store);

	// Get appId from current route
	const currentAppId = getAppIdParam(currentRoute);

	// Get appId from next route (if nextState is provided)
	const nextAppId = nextState ? getAppIdFromUrl(nextState.url) : null;

	// Clear app state if appIds are different
	if (currentAppId !== nextAppId) {
		clearAppStateOnceOutsideApp(router, store, applicationsSvc, currentAppId);
	}

	return true;
};

/**
 * Waits for the navigation that triggered the guard to settle, and clears the
 * stored app only when it really ended up outside it.
 *
 * Nothing is cleared when the navigation is cancelled (an unsaved-changes
 * dialog, another guard), nor when a resolver has already loaded a different
 * app in the meantime — that app is the one on screen now.
 *
 * @param router - Router whose events report the outcome of the navigation
 * @param store - Store holding the app state
 * @param applicationsSvc - Source of the app currently held in the store
 * @param currentAppId - appId of the route being left
 */
function clearAppStateOnceOutsideApp(
	router: Router,
	store: Store,
	applicationsSvc: ApplicationsService,
	currentAppId: string | null
): void {
	router.events
		.pipe(
			filter(
				(event) =>
					event instanceof NavigationEnd ||
					event instanceof NavigationCancel ||
					event instanceof NavigationError ||
					event instanceof NavigationSkipped
			),
			take(1)
		)
		.subscribe((event) => {
			if (!(event instanceof NavigationEnd)) {
				return;
			}

			if (getAppIdFromUrl(event.urlAfterRedirects) === currentAppId) {
				return;
			}

			const storedAppId = applicationsSvc.appId();

			if (currentAppId !== (storedAppId === null ? null : `${storedAppId}`)) {
				return;
			}

			store.dispatch(loadAppSuccess({ app: null }));
		});
}

/**
 * Recursively searches for the appId parameter in the route or any of its parent routes
 *
 * @param route - The route snapshot to check
 * @returns The appId value if found, null otherwise
 */
function getAppIdParam(route: ActivatedRouteSnapshot): string | null {
	if (route.paramMap.has('appId')) {
		return route.paramMap.get('appId');
	}

	// Check parent routes recursively
	if (route.parent) {
		return getAppIdParam(route.parent);
	}

	return null;
}

/**
 * Extracts the appId from a URL
 *
 * @param url - The URL to parse
 * @returns The appId if found in the URL, null otherwise
 */
function getAppIdFromUrl(url: string): string | null {
	// Match patterns like /apps/123 or /app/123 and extract the ID
	const appIdPattern = /\/apps?\/(\d+)/;
	const match = url.match(appIdPattern);
	return match ? match[1] : null;
}
